import { useEffect, useRef, useState } from "react";
import { Button, Input, Segmented, Tooltip, message } from "antd";
import {
  AlignCenterOutlined,
  AlignLeftOutlined,
  AlignRightOutlined,
  BoldOutlined,
  ClearOutlined,
  CodeOutlined,
  EyeOutlined,
  FontColorsOutlined,
  ItalicOutlined,
  LinkOutlined,
  OrderedListOutlined,
  RedoOutlined,
  SaveOutlined,
  StrikethroughOutlined,
  UnderlineOutlined,
  UndoOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";

import CompanyLogo from "../../assets/image/header-logo.png";
import apiClient from "../../actions/api";
import openNotification from "../../components/Notifications/openNotification";
import "../../style/auth/emailTemplate.scss";

const DEFAULT_SUBJECT = "{{company_name}} - Interview: {{candidate_name}}";
const DEFAULT_TEMPLATE = `
<div style="font-family: Arial, Helvetica, sans-serif; color: #202637; font-size: 15px; line-height: 1.55;">
  <p>Hi {{candidate_first_name}},</p>

  <p>Hope this note finds you well! I'm excited to connect and chat about the <strong>{{job_title}}</strong> role at <strong>{{company_name}}</strong>. Rather than playing email tag, I'd love to schedule a time for us to talk.</p>

  <p>Please let me know your availability for <strong>{{interview_date_range}}</strong>.</p>

  <p>If something would prevent you from attending the interview, please let me know.</p>

  <p>I can't wait to hear more about your journey and what's sparked your interest in this role—and promise, I'll bring plenty of details about the team and opportunity too!</p>

  <p style="margin-bottom: 20px;">Looking forward to our conversation!</p>

  <div style="color: #2196f3; font-weight: 700; line-height: 1.35;">
    <div>{{sender_name}}</div>
    <div>{{sender_title}}</div>
    <div>{{sender_phone}}</div>
  </div>

  <div style="margin-top: 14px;">
    <img src="${CompanyLogo}" alt="{{company_name}}" style="display: block; width: 220px; max-width: 100%; height: auto; object-fit: contain; object-position: left center;" />
  </div>
</div>`.trim();

const showApiError = (error, fallbackMessage) => {
  const errors = error?.response?.data?.errors;
  if (Array.isArray(errors) && errors.length) {
    errors.forEach((item) =>
      openNotification(
        "error",
        "Error",
        typeof item === "string" ? item : item.msg,
      ),
    );
    return;
  }

  openNotification(
    "error",
    "Error",
    error?.response?.data?.message || fallbackMessage,
  );
};

const EmailTemplate = ({ title }) => {
  document.title = title;

  const editorRef = useRef(null);
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [content, setContent] = useState(DEFAULT_TEMPLATE);
  const [mode, setMode] = useState("visual");
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (mode === "visual" && !loading && editorRef.current) {
      editorRef.current.innerHTML = content;
    }
  }, [mode, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let active = true;

    apiClient
      .get("/email-templates/get")
      .then((response) => {
        if (!active) return;

        const template =
          response.data?.emailTemplate ??
          response.data?.template ??
          response.data;

        if (template && (template.subject || template.content)) {
          setSubject(template.subject || DEFAULT_SUBJECT);
          setContent(template.content || DEFAULT_TEMPLATE);
        }
      })
      .catch((error) => {
        if (active) {
          showApiError(
            error,
            "The email template could not be loaded. The default template is shown.",
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const runCommand = (command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    setContent(editorRef.current?.innerHTML || "");
  };

  const addLink = () => {
    const url = window.prompt("Enter the link URL");
    if (url) runCommand("createLink", url);
  };

  const handleSave = async () => {
    if (!subject.trim()) {
      message.warning("Please enter an email subject.");
      return;
    }

    if (!content.trim()) {
      message.warning("Please enter the email content.");
      return;
    }

    setSaving(true);
    try {
      const response = await apiClient.post("/email-templates/update", {
        subject: subject.trim(),
        content,
      });
      const template =
        response.data?.emailTemplate ??
        response.data?.template ??
        response.data;

      if (template?.subject) setSubject(template.subject);
      if (template?.content) setContent(template.content);
      message.success("Email template saved to the database.");
    } catch (error) {
      showApiError(error, "The email template could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  const toolbar = [
    ["Bold", <BoldOutlined />, "bold"],
    ["Italic", <ItalicOutlined />, "italic"],
    ["Underline", <UnderlineOutlined />, "underline"],
    ["Strikethrough", <StrikethroughOutlined />, "strikeThrough"],
    ["Bulleted list", <UnorderedListOutlined />, "insertUnorderedList"],
    ["Numbered list", <OrderedListOutlined />, "insertOrderedList"],
    ["Align left", <AlignLeftOutlined />, "justifyLeft"],
    ["Align center", <AlignCenterOutlined />, "justifyCenter"],
    ["Align right", <AlignRightOutlined />, "justifyRight"],
    ["Undo", <UndoOutlined />, "undo"],
    ["Redo", <RedoOutlined />, "redo"],
    ["Clear formatting", <ClearOutlined />, "removeFormat"],
  ];

  return (
    <section className="email-template-section">
      <div className="email-template-heading">
        <div>
          <span className="eyebrow">Communication</span>
          <h1>Email Template</h1>
          <p>Create a reusable, fully formatted HTML email.</p>
        </div>
        <div className="heading-actions">
          <Button
            icon={<EyeOutlined />}
            onClick={() => setShowPreview((value) => !value)}
          >
            {showPreview ? "Hide preview" : "Preview"}
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            disabled={loading}
            onClick={handleSave}
          >
            Save template
          </Button>
        </div>
      </div>

      <div
        className={`email-template-layout ${showPreview ? "with-preview" : ""}`}
      >
        <div className="template-card">
          <div className="field-block">
            <label htmlFor="email-template-subject">Subject</label>
            <Input
              id="email-template-subject"
              size="large"
              value={subject}
              disabled={loading}
              maxLength={200}
              placeholder="e.g. Your application for {{job_title}}"
              onChange={(event) => setSubject(event.target.value)}
              showCount
            />
          </div>

          <div className="editor-label-row">
            <div>
              <label>Email content</label>
              <span>Use the visual editor or edit the HTML directly.</span>
            </div>
            <Segmented
              value={mode}
              onChange={setMode}
              options={[
                {
                  label: "Visual",
                  value: "visual",
                  icon: <FontColorsOutlined />,
                },
                { label: "HTML", value: "html", icon: <CodeOutlined /> },
              ]}
            />
          </div>

          {mode === "visual" ? (
            <div className="rich-editor">
              <div className="editor-toolbar">
                <select
                  aria-label="Text style"
                  defaultValue="p"
                  onChange={(event) =>
                    runCommand("formatBlock", event.target.value)
                  }
                >
                  <option value="p">Paragraph</option>
                  <option value="h1">Heading 1</option>
                  <option value="h2">Heading 2</option>
                  <option value="h3">Heading 3</option>
                  <option value="blockquote">Quote</option>
                </select>
                <span className="toolbar-divider" />
                {toolbar.map(([label, icon, command]) => (
                  <Tooltip title={label} key={command}>
                    <button
                      type="button"
                      aria-label={label}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => runCommand(command)}
                    >
                      {icon}
                    </button>
                  </Tooltip>
                ))}
                <Tooltip title="Add link">
                  <button
                    type="button"
                    aria-label="Add link"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={addLink}
                  >
                    <LinkOutlined />
                  </button>
                </Tooltip>
              </div>
              <div
                ref={editorRef}
                className="editor-canvas"
                contentEditable={!loading}
                aria-disabled={loading}
                suppressContentEditableWarning
                onInput={(event) => setContent(event.currentTarget.innerHTML)}
                aria-label="Email content editor"
              />
            </div>
          ) : (
            <Input.TextArea
              className="html-editor"
              value={content}
              disabled={loading}
              onChange={(event) => setContent(event.target.value)}
              autoSize={{ minRows: 18, maxRows: 30 }}
              spellCheck={false}
              aria-label="Email HTML source"
            />
          )}

          <div className="template-hint">
            Personalize your template with variables such as{" "}
            <code>{"{{candidate_first_name}}"}</code>,{" "}
            <code>{"{{job_title}}"}</code>, <code>{"{{company_name}}"}</code>,
            and <code>{"{{candidate_name}}"}</code>.
          </div>
        </div>

        {showPreview && (
          <aside className="preview-card">
            <div className="preview-card-header">
              <span>Live preview</span>
              <small>Desktop email</small>
            </div>
            <div className="preview-subject">
              <small>Subject</small>
              <strong>{subject || "Your email subject"}</strong>
            </div>
            <iframe
              title="Email template preview"
              sandbox=""
              srcDoc={`<!doctype html><html><head><meta charset="utf-8"><style>body{font-family:Arial,sans-serif;color:#202637;line-height:1.6;padding:24px;margin:0}img{max-width:100%;height:auto}a{color:#087b85}</style></head><body>${content}</body></html>`}
            />
          </aside>
        )}
      </div>
    </section>
  );
};

export default EmailTemplate;
